import { Button, Chip } from '@heroui/react'
import { type TenantDto } from '@qse/api'
import { type ModalComponentProps } from '@qse/constants'
import moment from 'moment'

interface ViewTenantProps extends ModalComponentProps {
  data: {
    tenant: TenantDto
  }
}

export default function ViewTenant({ setModelActive, onClose, data }: ViewTenantProps) {
  const { tenant } = data

  const handleClose = () => {
    setModelActive(undefined)
    onClose()
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success'
      case 'suspended':
        return 'danger'
      case 'pending_confirmation':
        return 'warning'
      case 'incomplete':
        return 'warning'
      case 'trialing':
        return 'primary'
      case 'cancelled':
        return 'warning'
      default:
        return 'default'
    }
  }

  const formatDate = (dateString: string) => {
    return moment(dateString).format('MM/DD/YYYY')
  }

  const getTenantType = () => {
    return tenant.resellerId === '876e4567-e89b-12d3-a456-426614174001'
      ? 'QSE Enterprise'
      : 'QSE Retail'
  }

  const getNextBillingDate = () => {
    if (!tenant.nextBillingCycleDate) return 'N/A'

    const nextBilling = moment(tenant.nextBillingCycleDate).format('MM/DD/YYYY')

    return nextBilling
  }

  return (
    <div className="flex flex-col w-full max-w-2xl">
      <div className="flex flex-col gap-6 py-6 w-full">
        {/* Tenant Information Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Tenant Type */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
              Tenant Type
            </label>
            <p className="text-base text-gray-900 dark:text-white font-medium">{getTenantType()}</p>
          </div>

          {/* Company Name */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
              Company Name
            </label>
            <p
              className="text-base text-gray-900 dark:text-white font-medium break-words"
              title={tenant.name}
            >
              {tenant.name}
            </p>
          </div>

          {/* Primary Contact Name */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
              Primary Contact
            </label>
            <p className="text-base text-gray-900 dark:text-white font-medium">
              {tenant.firstName && tenant.lastName
                ? `${tenant.firstName} ${tenant.lastName}`
                : 'N/A'}
            </p>
          </div>

          {/* Contact Email */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
              Contact Email
            </label>
            <p className="text-base text-gray-900 dark:text-white font-medium break-all">
              {tenant.adminEmail || 'N/A'}
            </p>
          </div>

          {/* Current Plan */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
              Current Plan
            </label>
            <p className="text-base text-gray-900 dark:text-white font-medium">
              {tenant.plan?.name || 'N/A'}
            </p>
          </div>

          {/* Tenant Status */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Status</label>
            <div>
              <Chip
                className="capitalize rounded font-medium"
                color={getStatusColor(tenant.status)}
                size="sm"
                variant="flat"
              >
                {tenant.status}
              </Chip>
            </div>
          </div>

          {/* Creation Date */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
              Creation Date
            </label>
            <p className="text-base text-gray-900 dark:text-white font-medium">
              {formatDate(tenant.createdAt)}
            </p>
          </div>

          {/* Next Billing Cycle Date */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
              Next Billing Date
            </label>
            <p className="text-base text-gray-900 dark:text-white font-medium">
              {getNextBillingDate()}
            </p>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-4 mt-2 border-t border-gray-200 dark:border-gray-700">
        <Button className="px-6" color="primary" radius="sm" variant="flat" onPress={handleClose}>
          Close
        </Button>
      </div>
    </div>
  )
}
